import { useEffect } from 'react';
import Container from '@v2/shared/components/Container';
import Spinner from '@v2/shared/components/Spinner';
import { Product } from '@v2/shared/lib/types';

const SRC = 'https://www.sitejabber.com/js/v2/5f76395cc3996/widgets.js';

const WIDGET_CLASS = 'stjr-product-review-page';

type Props = {
  product: Product;
};

/**
 * SiteJabber's reviews for this product, as a full-width panel with its own heading.
 *
 * Shared rather than owned by a page: the Magzter Gold and Zinio Unlimited landing pages
 * both end on it, identically, and it was already written once for the first of them.
 * The `id="reviews"` is part of the contract — both heroes' star ratings scroll to it.
 *
 * The homepage's CompanyReviewScript is the same idea for the company-wide carousel;
 * this one asks for the product widget, which needs two things the other does not — the
 * widget class in the script url, and a ProductJson block describing what is being
 * reviewed.
 *
 * That block is built from `reviews_data.product` rather than from the product's own
 * fields, because three of the things SiteJabber wants are not on a product: the
 * description, the gtin (Product.php derives it from the issn) and the publisher's
 * brand. `reviews_data` also resolves to the *parent* title for a single issue, which
 * is where its reviews actually live.
 *
 * The loader is appended imperatively for the reason set out on CompanyReviewScript:
 * React DOM builds script elements in a way that marks them parser-inserted, so a
 * <script src> written as JSX lands in the DOM looking right and is never fetched.
 * The ProductJson block below is exempt — it is `application/json`, which nothing
 * executes; the widget reads it as data.
 */
export default function ProductReviewsPanel({ product }: Props) {
  const reviewsProduct = product.reviews_data?.product;

  useEffect(() => {
    if (!reviewsProduct || document.querySelector(`script[src^="${SRC}"]`)) {
      return;
    }

    const script = document.createElement('script');
    script.src = `${SRC}?widget-classes=${WIDGET_CLASS}`;
    script.async = true;

    document.body.appendChild(script);
  }, [reviewsProduct]);

  if (!reviewsProduct) {
    return null;
  }

  return (
    <div className="flex justify-center bg-white py-10 md:py-12 lg:py-20" id="reviews">
      <Container className="flex flex-col items-center justify-center">
        <h2 className="text-3xl md:text-4xl mb-10 font-bold text-center">Reviews</h2>
        <script
          id="ProductJson-product-template"
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              id: reviewsProduct.id,
              sku: reviewsProduct.sku,
              title: reviewsProduct.name,
              description: reviewsProduct.description,
              price: reviewsProduct.price,
              gtin: reviewsProduct.gtin,
              brand: reviewsProduct.brand,
              product_link: reviewsProduct.url,
              images: reviewsProduct.image ? [reviewsProduct.image] : [],
              item_group: reviewsProduct.id,
            }),
          }}
        />
        <div className={`${WIDGET_CLASS} w-full`} data-product-id={reviewsProduct.sku}>
          <div className="text-center">
            <Spinner className="text-4xl" />
          </div>
        </div>
      </Container>
    </div>
  );
}
